// FILE: PullRequestQaActions.tsx
// Purpose: What a reviewer can do with a pull request that is not a GitHub
//          write — try it on the shared environment, or give it a preview of
//          its own — from the pull request panel.
// Layer: Component
// Exports: PullRequestQaActions
//
// Why it lives beside the merge buttons and not among them: those four write to
// GitHub and ask for `p.git_write`; these write nothing there. They spend the
// disk and the workspaces of *this* installation, so they ask for
// `p.pr.preview` and they are absent — not greyed out — for anybody without it.
//
// The two halves answer the same request, "let me click through PR 131", and
// the whole reason both exist is that they trade differently:
//
//   - «Probar sobre <entorno>» puts the branch on the environment everybody
//     shares. One pull request at a time, the PM's data is what the migrations
//     run against, and taking it off is a deliberate act.
//   - «Preview aislada» builds a worktree, a copy of the data and a URL of its
//     own. Several at once, nothing shared is touched, and it expires by itself.
//
// Like every other environment operation in this product, none of these buttons
// operate anything. They open a thread with an agent and the message already
// written, and a person presses Send. The indirection is the feature: both
// halves run migrations, and the thread is where the agent says what it found.
// The one exception is in the environment card, where extending and destroying
// a preview are direct — see `PrPreviewList`.

import type { EnvironmentId, ProjectId, QaOperationKind } from "@taskin-code/contracts";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { Button } from "~/components/ui/button";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "~/components/ui/menu";
import { useHasProjectPermission } from "~/auth/authStore";
import {
  type AdminEnvironmentRow,
  EnvironmentsClient,
  type EnvironmentPromptSet,
} from "~/auth/EnvironmentsClient";
import { projectEnvironmentsQueryOptions } from "~/hooks/useThreadEnvironment";
import { environmentQueryKeys } from "~/lib/environmentQueries";
import { openNewThreadDialog } from "~/newThreadDialogStore";
import { previewUrl } from "~/components/settings/EnvironmentsSection.logic";
import {
  environmentTryingPullRequest,
  projectEnvironmentsOf,
  trialCandidatesOf,
} from "~/components/settings/projectEnvironments.logic";
import { previewOfPullRequest } from "~/components/settings/prPreviews.logic";

const environmentName = (row: AdminEnvironmentRow): string => row.name ?? row.branch;

/** The server renders one message per operation; this picks the right one. */
const promptFor = (operation: QaOperationKind, prompts: EnvironmentPromptSet): string => {
  switch (operation) {
    case "try-pr":
      return prompts.tryPr;
    case "back-to-base":
      return prompts.backToBase;
    case "preview-create":
      return prompts.previewCreate;
    case "preview-destroy":
      return prompts.previewDestroy;
    case "sync":
      return prompts.sync;
    case "reset-data":
      return prompts.resetData;
    case "clone":
      return prompts.clone;
  }
};

export function PullRequestQaActions({
  projectId,
  prNumber,
  disabled = false,
}: {
  readonly projectId: ProjectId;
  readonly prNumber: number;
  /** True while a GitHub write is in flight, so nothing else is clickable. */
  readonly disabled?: boolean;
}) {
  const canPreview = useHasProjectPermission(projectId, "p.pr.preview");
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The same query the chat header's badge uses, so the two agree about which
  // environment is wearing what — and so opening the panel costs no request the
  // app was not already making.
  const environmentsQuery = useQuery({
    ...projectEnvironmentsQueryOptions(canPreview ? projectId : null),
    enabled: canPreview,
  });
  // Under `environmentQueryKeys`, so it is the same cache entry the environment
  // card reads and the same one `pr-preview.*` domain events invalidate: a
  // preview built from a thread appears here without this panel polling.
  const previewsQuery = useQuery({
    queryKey: environmentQueryKeys.prPreviews(projectId),
    queryFn: () => EnvironmentsClient.listPrPreviews(projectId),
    enabled: canPreview,
    staleTime: 30_000,
    retry: false,
  });

  // Nothing at all without the grant: these buttons are the whole feature, and
  // the panel around them is governed by a different permission.
  if (!canPreview) return null;

  const environments = environmentsQuery.data ?? [];
  const trialling = environmentTryingPullRequest(environments, prNumber);
  const candidates = trialCandidatesOf(environments);
  const preview = previewOfPullRequest(previewsQuery.data ?? [], prNumber);
  // The environment whose lock the preview's teardown thread will describe. A
  // preview outlives the environment it came from, so this can be null.
  const previewBase =
    preview === null
      ? null
      : (environments.find((row) => row.id === preview.baseEnvironmentId) ?? null);
  // Said out loud when it is the reason there is nothing to offer: a panel with
  // no buttons and no explanation reads as broken, and «probar» being
  // unavailable because somebody else is testing is worth one line.
  const blockedByTrial =
    trialling === null && candidates.length === 0
      ? (projectEnvironmentsOf(environments).find((row) => row.activeTrial !== null) ?? null)
      : null;

  if (
    trialling === null &&
    preview === null &&
    candidates.length === 0 &&
    blockedByTrial === null
  ) {
    return null;
  }

  /**
   * Fetch the message, then open the dialog in its «operación de entorno» mode.
   *
   * The prompt is rendered by the server — it mixes the environment row, the
   * project's title and the project's own overrides — so the button waits for it
   * rather than opening a dialog it would have to fill in afterwards.
   */
  const openOperation = async (environment: AdminEnvironmentRow, operation: QaOperationKind) => {
    setError(null);
    setPreparing(true);
    try {
      const prompts = await EnvironmentsClient.prompts(environment.id as EnvironmentId, {
        prNumber,
      });
      openNewThreadDialog({
        preferredProjectId: projectId,
        environmentId: environment.id as EnvironmentId,
        qaOperation: operation,
        initialPrompt: promptFor(operation, prompts),
      });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo preparar el mensaje del hilo.");
    } finally {
      setPreparing(false);
    }
  };

  const busy = disabled || preparing;

  /**
   * The in-place half.
   *
   * Already on: the useful buttons are the preview and the way back. Offering
   * «Probar» again would only produce the server's "ya se está probando".
   */
  const trialPart =
    trialling !== null ? (
      <>
        <span className="text-[11px] text-sky-600 dark:text-sky-300">
          Montado sobre {environmentName(trialling)}
        </span>
        {previewUrl(trialling) === null ? (
          <span className="text-[11px] text-muted-foreground">preview sin arrancar</span>
        ) : (
          <Button
            size="xs"
            variant="outline"
            render={<a href={previewUrl(trialling)!} target="_blank" rel="noreferrer" />}
          >
            Ver preview
          </Button>
        )}
        <Button
          size="xs"
          variant="outline"
          disabled={busy}
          onClick={() => void openOperation(trialling, "back-to-base")}
        >
          Volver a la base
        </Button>
      </>
    ) : candidates.length === 1 ? (
      // One environment is the common case and deserves one click; several is a
      // choice, and a menu is the only honest way to ask it.
      <Button
        size="xs"
        variant="outline"
        disabled={busy}
        onClick={() => void openOperation(candidates[0]!, "try-pr")}
      >
        {preparing ? "Preparando…" : `Probar sobre ${environmentName(candidates[0]!)}`}
      </Button>
    ) : candidates.length > 1 ? (
      <Menu>
        <MenuTrigger render={<Button size="xs" variant="outline" disabled={busy} />}>
          {preparing ? "Preparando…" : "Probar sobre…"}
        </MenuTrigger>
        <MenuPopup align="start">
          {candidates.map((candidate) => (
            <MenuItem key={candidate.id} onClick={() => void openOperation(candidate, "try-pr")}>
              {environmentName(candidate)}
            </MenuItem>
          ))}
        </MenuPopup>
      </Menu>
    ) : null;

  /**
   * The isolated half.
   *
   * When this pull request already has one, the useful buttons are its URL and
   * its teardown: `qa_preview_create` is idempotent, so asking again would hand
   * back the same preview and teach nobody anything.
   *
   * Note it is offered from the same list of candidates as a trial. A preview is
   * branched from the environment's *current* commit, so building one off an
   * environment that is wearing another pull request would quietly include that
   * pull request's commits in this review.
   */
  const previewPart =
    preview !== null ? (
      <>
        <span className="text-[11px] text-muted-foreground">
          Preview aislada: {preview.status === "ready" ? "lista" : preview.status}
        </span>
        {preview.url !== null ? (
          <Button
            size="xs"
            variant="outline"
            render={<a href={preview.url} target="_blank" rel="noreferrer" />}
          >
            Ver preview aislada
          </Button>
        ) : null}
        {previewBase !== null ? (
          <Button
            size="xs"
            variant="outline"
            className="text-red-500 hover:text-red-400"
            disabled={busy}
            onClick={() => void openOperation(previewBase, "preview-destroy")}
          >
            Destruir preview
          </Button>
        ) : null}
      </>
    ) : candidates.length === 1 ? (
      <Button
        size="xs"
        variant="outline"
        disabled={busy}
        onClick={() => void openOperation(candidates[0]!, "preview-create")}
      >
        {preparing ? "Preparando…" : "Preview aislada"}
      </Button>
    ) : candidates.length > 1 ? (
      <Menu>
        <MenuTrigger render={<Button size="xs" variant="outline" disabled={busy} />}>
          {preparing ? "Preparando…" : "Preview aislada desde…"}
        </MenuTrigger>
        <MenuPopup align="start">
          {candidates.map((candidate) => (
            <MenuItem
              key={candidate.id}
              onClick={() => void openOperation(candidate, "preview-create")}
            >
              {environmentName(candidate)}
            </MenuItem>
          ))}
        </MenuPopup>
      </Menu>
    ) : null;

  return (
    <>
      {trialPart}
      {previewPart}
      {blockedByTrial !== null ? (
        <span className="text-[11px] text-muted-foreground">
          {environmentName(blockedByTrial)} está probando el PR #
          {blockedByTrial.activeTrial?.prNumber}: hay que devolverlo a su base antes de montar este.
        </span>
      ) : null}
      {error !== null ? <span className="text-[11px] text-red-400">{error}</span> : null}
    </>
  );
}
